import { useEffect, useMemo, useState } from "react";
import { CheckCircle } from "@phosphor-icons/react";

const PHASES = [
    { keys: ["queued", "documents"], label: "Recupero documenti", sub: "Scarico perizia, avviso e ordinanza dagli archivi del tribunale" },
    { keys: ["ocr"], label: "Lettura OCR", sub: "Estraggo il testo dalla perizia, pagina per pagina" },
    { keys: ["preparing", "doc_analysis", "analysis"], label: "Analisi documentale", sub: "L'AI legge la perizia: superficie, stato, occupazione, vincoli" },
    { keys: ["risk"], label: "Rischi legali e urbanistici", sub: "Formalità da cancellare, abusi edilizi, conformità catastale" },
    { keys: ["market"], label: "Valutazione di mercato", sub: "Comparabili di zona e valore realistico di rivendita" },
    { keys: ["investment"], label: "Analisi d'investimento", sub: "ROI netto post-tasse nei 3 scenari: prudente, realistico, aggressivo" },
    { keys: ["report"], label: "Report finale", sub: "Compongo il report professionale e calcolo l'Investment Score" },
];

const TICKER = [
    "Verifico la trascrizione del pignoramento…",
    "Controllo lo stato di occupazione dell'immobile…",
    "Confronto il prezzo base con i valori di zona…",
    "Calcolo l'imposta di registro con la regola prezzo-valore…",
    "Stimo i costi di ristrutturazione dalla perizia…",
    "Applico la plusvalenza 26% sul profitto lordo…",
    "Cerco formalità pregiudizievoli da cancellare…",
    "Valuto il rischio di rilanci in asta…",
];

function phaseIndex(stage) {
    const idx = PHASES.findIndex((p) => p.keys.includes(stage));
    return idx === -1 ? 2 : idx;
}

function scoreTone(s) {
    if (s <= 40) return { color: "#f87171", label: "SCARTA" };
    if (s <= 60) return { color: "#fb923c", label: "ALTO RISCHIO" };
    if (s <= 80) return { color: "#facc15", label: "INTERESSANTE" };
    return { color: "#34d399", label: "OTTIMA" };
}

function ScoreReveal({ score }) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        const start = performance.now();
        let raf;
        const step = (t) => {
            const p = Math.min(1, (t - start) / 1800);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(eased * score));
            if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [score]);
    const tone = scoreTone(score);
    return (
        <div className="absolute inset-0 flex items-center justify-center pt-16 pointer-events-none theater-score" data-testid="theater-score-reveal">
            <div className="text-center px-6 py-4" style={{ background: "rgba(6,10,20,0.72)", backdropFilter: "blur(3px)", border: `1px solid ${tone.color}55`, boxShadow: `0 0 42px ${tone.color}33` }}>
                <div className="text-[9px] font-bold uppercase tracking-[0.24em] text-slate-400 mb-1">Investment Score</div>
                <div className="font-mono-tight font-bold leading-none" style={{ fontSize: "56px", color: tone.color }}>{val}</div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] mt-1.5" style={{ color: tone.color }}>{tone.label}</div>
            </div>
        </div>
    );
}

export default function AnalysisTheater({ stage, agents = [], previewScore }) {
    const cur = phaseIndex(stage || "analysis");
    const [tick, setTick] = useState(0);
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const t = setInterval(() => setTick((v) => v + 1), 4200);
        const e = setInterval(() => setElapsed((v) => v + 1), 1000);
        return () => { clearInterval(t); clearInterval(e); };
    }, []);

    const pct = Math.min(96, Math.round(((cur + 0.6) / PHASES.length) * 100));
    const mm = String(Math.floor(elapsed / 60)).padStart(1, "0");
    const ss = String(elapsed % 60).padStart(2, "0");
    const doneAgents = useMemo(() => agents.filter((a) => a.status === "success").length, [agents]);

    return (
        <div className="fd mb-6 fade-up overflow-hidden border border-[#1a2436] bg-[#0a0f1c] text-white" data-testid="analysis-running">
            <div className="theater-grid absolute" />
            <div className="flex flex-col lg:flex-row">
                {/* LEFT: the building */}
                <div className="relative lg:w-[46%] flex items-end justify-center pt-8 px-6 theater-bg">
                    <TheaterBuilding phase={cur} />
                    {cur >= 6 && previewScore != null && <ScoreReveal score={previewScore} />}
                    <div className="absolute top-5 left-6 right-6 flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <span className="w-2 h-2 rounded-full bg-emerald-400 theater-blink" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300">
                                Analisi in corso
                            </span>
                        </div>
                        <span className="font-mono-tight text-[11px] text-slate-400" data-testid="theater-elapsed">{mm}:{ss}</span>
                    </div>
                </div>

                {/* RIGHT: stage timeline */}
                <div className="flex-1 p-6 md:p-8 border-t lg:border-t-0 lg:border-l border-[#1a2436]">
                    <div className="flex items-baseline justify-between mb-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
                            Pipeline · 8 agenti AI
                        </div>
                        <div className="font-mono-tight text-2xl font-bold text-emerald-400" data-testid="theater-pct">{pct}%</div>
                    </div>
                    <div className="h-[3px] w-full bg-[#1a2436] mb-6 overflow-hidden">
                        <div className="h-full bg-emerald-400 transition-all duration-1000 ease-out" style={{ width: `${pct}%` }} />
                    </div>

                    <div className="space-y-0" data-testid="analysis-stage-progress">
                        {PHASES.map((p, i) => {
                            const done = i < cur;
                            const active = i === cur;
                            return (
                                <div key={p.label} data-testid={`stage-${p.keys[0]}`} className="flex gap-3.5 group">
                                    <div className="flex flex-col items-center">
                                        <div className={`w-[22px] h-[22px] shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold border transition-all duration-500 ${
                                            done ? "bg-emerald-400 border-emerald-400 text-[#0a0f1c]"
                                            : active ? "border-emerald-400 text-emerald-300 theater-ring"
                                            : "border-[#2a3650] text-slate-500"}`}>
                                            {done ? <CheckCircle size={13} weight="bold" /> : i + 1}
                                        </div>
                                        {i < PHASES.length - 1 && (
                                            <div className={`w-px flex-1 min-h-[14px] my-1 transition-colors duration-700 ${done ? "bg-emerald-400/60" : "bg-[#2a3650]"}`} />
                                        )}
                                    </div>
                                    <div className={`pb-4 transition-opacity duration-500 ${active ? "opacity-100" : done ? "opacity-60" : "opacity-30"}`}>
                                        <div className={`text-[13px] font-semibold leading-tight ${active ? "text-white" : "text-slate-300"}`}>
                                            {p.label}
                                        </div>
                                        {active && (
                                            <div className="text-[11px] text-slate-400 mt-0.5 leading-relaxed fade-up">{p.sub}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-2 pt-4 border-t border-[#1a2436] flex items-center justify-between gap-4">
                        <div className="font-mono-tight text-[11px] text-emerald-300/80 theater-ticker truncate" key={tick} data-testid="theater-ticker">
                            ▸ {TICKER[tick % TICKER.length]}
                        </div>
                        <div className="text-[10px] text-slate-400 shrink-0 font-mono-tight">{doneAgents}/7 agenti</div>
                    </div>
                    <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">
                        Tempo stimato 2–3 minuti. Puoi lasciare questa pagina: l'analisi continua e la troverai pronta in dashboard.
                    </p>
                </div>
            </div>
        </div>
    );
}

function TheaterBuilding({ phase }) {
    const on = (n) => phase >= n;
    return (
        <svg viewBox="0 0 340 300" className="w-full max-w-[340px]" aria-hidden="true">
            {/* blueprint ghost outline — always visible, drawn */}
            <g stroke="#2d3f63" strokeWidth="1" fill="none" strokeDasharray="4 4" className="theater-dash">
                <rect x="90" y="60" width="160" height="210" />
                <line x1="90" y1="120" x2="250" y2="120" />
                <line x1="90" y1="175" x2="250" y2="175" />
                <line x1="90" y1="230" x2="250" y2="230" />
                <path d="M 90 60 L 170 22 L 250 60" />
            </g>

            {/* ground */}
            <line x1="20" y1="270" x2="320" y2="270" stroke="#2a3650" strokeWidth="1.5" />

            {/* phase 0: foundation */}
            <g className="theater-layer" style={{ opacity: on(0) ? 1 : 0 }}>
                <rect x="82" y="262" width="176" height="8" fill="#16213a" stroke="#3b82f6" strokeWidth="1" />
            </g>

            {/* phase 2: ground floor */}
            <g className="theater-layer" style={{ opacity: on(2) ? 1 : 0 }}>
                <rect x="90" y="230" width="160" height="32" fill="#101a30" stroke="#4a5f8f" strokeWidth="1.2" />
                <rect x="155" y="238" width="30" height="24" fill="#0a0f1c" stroke="#4a5f8f" strokeWidth="1" />
            </g>

            {/* phase 3: floor 2 + shield */}
            <g className="theater-layer" style={{ opacity: on(3) ? 1 : 0 }}>
                <rect x="90" y="175" width="160" height="55" fill="#101a30" stroke="#4a5f8f" strokeWidth="1.2" />
                <line x1="90" y1="202" x2="250" y2="202" stroke="#2a3650" strokeWidth="0.7" />
            </g>

            {/* phase 4: floor 3 + market bars */}
            <g className="theater-layer" style={{ opacity: on(4) ? 1 : 0 }}>
                <rect x="90" y="120" width="160" height="55" fill="#101a30" stroke="#4a5f8f" strokeWidth="1.2" />
                <g fill="#1e2f52">
                    <rect x="34" y="238" width="12" height="32" />
                    <rect x="52" y="222" width="12" height="48" />
                    <rect x="286" y="230" width="12" height="40" />
                    <rect x="304" y="248" width="12" height="22" />
                </g>
                <polyline points="30,215 58,200 286,222 312,240" fill="none" stroke="#3b82f6" strokeWidth="0" />
            </g>

            {/* phase 5: top + roof + lit windows */}
            <g className="theater-layer" style={{ opacity: on(5) ? 1 : 0 }}>
                <rect x="90" y="60" width="160" height="60" fill="#101a30" stroke="#4a5f8f" strokeWidth="1.2" />
                <path d="M 90 60 L 170 22 L 250 60 Z" fill="#0e1830" stroke="#5b7ab8" strokeWidth="1.2" />
            </g>
            {on(5) && (
                <g className="theater-windows">
                    {[
                        [104, 74], [136, 74], [186, 74], [218, 74],
                        [104, 134], [136, 134], [186, 134], [218, 134],
                        [104, 188], [136, 188], [186, 188], [218, 188],
                        [104, 240], [218, 240],
                    ].map(([x, y], i) => (
                        <rect key={i} x={x} y={y} width="18" height="26" fill="#f5c451"
                            style={{ animationDelay: `${i * 0.12}s` }} className="theater-window" />
                    ))}
                </g>
            )}

            {/* phase 6: final glow + radar */}
            {on(6) && (
                <g>
                    <circle cx="170" cy="150" r="120" fill="none" stroke="#34d399" strokeWidth="1" className="theater-radar" />
                    <circle cx="170" cy="150" r="120" fill="none" stroke="#34d399" strokeWidth="1" className="theater-radar" style={{ animationDelay: "1.2s" }} />
                </g>
            )}

            {/* OCR scan beam — visible during early phases */}
            {phase >= 1 && phase < 5 && (
                <rect x="82" y="20" width="176" height="3" fill="#34d399" opacity="0.7" className="theater-scan" />
            )}
        </svg>
    );
}
